import { useTranslation } from 'react-i18next';
import { MainLayout } from '../../../core/layouts/MainLayout';
import { Shield, Target, Award, Users, ArrowRight } from 'lucide-react';
import { RemoteImage } from '../../../core/components/RemoteImage';
import clsx from 'clsx';

export const AboutPage = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';

    const stats = [
        { label: t('about.stats.years', 'Years of Excellence'), value: '15' },
        { label: t('about.stats.projects', 'Projects Delivered'), value: '32' },
        { label: t('about.stats.families', 'Happy Families'), value: '2.5k' },
        { label: t('about.stats.team', 'Team Members'), value: '120' },
    ];

    const values = [
        {
            icon: <Shield className="w-6 h-6" />,
            title: t('about.values.integrity.title', 'Integrity'),
            description: t('about.values.integrity.desc', 'Trust through transparency.')
        },
        {
            icon: <Target className="w-6 h-6" />,
            title: t('about.values.vision.title', 'Vision'),
            description: t('about.values.vision.desc', 'Shaping future living.')
        },
        {
            icon: <Award className="w-6 h-6" />,
            title: t('about.values.quality.title', 'Quality'),
            description: t('about.values.quality.desc', 'Uncompromising standards.')
        },
        {
            icon: <Users className="w-6 h-6" />,
            title: t('about.values.community.title', 'Community'),
            description: t('about.values.community.desc', 'Fostering vibrant connections.')
        }
    ];

    return (
        <MainLayout forceSolidNavbar>
            <div className="bg-secondary min-h-screen text-white overflow-hidden">
                {/* Hero Section - Broken Grid */}
                <div className="relative pt-32 pb-20 sm:pt-48 sm:pb-32 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 relative">
                        {/* Vertical Text Decoration */}
                        <div className="absolute -left-4 top-0 h-full hidden lg:flex flex-col justify-between py-12 pointer-events-none opacity-20">
                            <div className="writing-vertical-rl text-xs uppercase tracking-[0.2em] rotate-180 border-l border-white/30 pl-4 h-full">
                                {t('about.est', 'Est. 2008 — Cairo, Egypt')}
                            </div>
                        </div>

                        {/* Text Block */}
                        <div className="lg:col-span-7 relative z-10 flex flex-col justify-end">
                            <h1 className="text-6xl sm:text-8xl font-display font-bold leading-[0.9] tracking-tighter mb-8 bg-clip-text text-transparent bg-gradient-to-br from-white to-white/50">
                                {t('about.t1', 'BUILDING')}<br />
                                <span className="text-stroke-white text-transparent">{t('about.t2', 'THE FUTURE')}</span>
                            </h1>
                            <div className="w-24 h-px bg-primary mb-8"></div>
                            <p className="text-xl sm:text-2xl text-slate-300 font-light max-w-xl leading-relaxed">
                                {t('about.subtitle', 'Create architectural fortresses that stand the test of time.')}
                            </p>
                        </div>

                        {/* Image Block - Overlapping */}
                        <div className="lg:col-span-5 relative mt-12 lg:mt-0">
                            <div className="relative aspect-[3/4] lg:absolute lg:top-[-20%] lg:right-0 lg:w-full">
                                <RemoteImage
                                    src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80"
                                    alt="Architecture"
                                    className="w-full h-full object-cover grayscale opacity-80 hover:grayscale-0 transition-all duration-700 Mirror ease-in-out"
                                />
                                {/* Border Frame */}
                                <div className="absolute -bottom-6 -left-6 w-full h-full border border-white/20 -z-10 hidden sm:block"></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats Section - Linear Strip */}
                <div className="border-y border-white/10 bg-secondary-light/30 backdrop-blur-sm">
                    <div className="max-w-7xl mx-auto">
                        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10 md:divide-x">
                            {stats.map((stat, index) => (
                                <div key={index} className="p-8 sm:p-12 text-center group hover:bg-white/5 transition-colors">
                                    <div className="text-4xl sm:text-6xl font-display font-bold text-white mb-2 group-hover:text-primary transition-colors">
                                        {stat.value}
                                    </div>
                                    <div className="text-xs uppercase tracking-widest text-slate-400">
                                        {stat.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Our Story - Architectural Layout */}
                <div className="py-24 sm:py-32 bg-surface text-secondary relative">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
                            {/* Image Composition */}
                            <div className="relative order-2 lg:order-1">
                                <div className="aspect-square relative z-10">
                                    <RemoteImage
                                        src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&q=80"
                                        alt="Construction"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                {/* Offset solid decoration */}
                                <div className="absolute -top-12 -left-12 w-64 h-64 bg-slate-200 z-0 hidden lg:block"></div>
                                {/* Floating text */}
                                <div className="absolute -bottom-8 -right-8 bg-white p-8 border border-slate-200 max-w-xs z-20 shadow-xl lg:shadow-none">
                                    <p className="font-display text-2xl font-bold leading-none whitespace-pre-line">
                                        {t('about.excellence', '"Excellence is\nour standard"')}
                                    </p>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="order-1 lg:order-2">
                                <div className="flex items-center gap-4 mb-8">
                                    <span className="h-px w-12 bg-secondary"></span>
                                    <span className="text-sm uppercase tracking-widest font-bold">{t('about.since', 'Since 2008')}</span>
                                </div>
                                <h2 className="text-4xl sm:text-5xl font-display font-bold mb-8 leading-tight">
                                    {t('about.story.title', 'Our Story')}
                                </h2>
                                <div className="prose prose-lg text-slate-600 prose-p:leading-relaxed">
                                    <p>
                                        {t('about.story.p1', 'Founded with a clear purpose, HESN Development has established itself as a pillar of reliability. Our name "HESN" (Fortress) reflects our philosophy.')}
                                    </p>
                                    <p>
                                        {t('about.story.p2', 'From our first project in New Cairo to our latest developments in the Capital, we have maintained a singular focus on quality engineering.')}
                                    </p>
                                </div>
                                <div className="mt-12">
                                    <button className="group flex items-center gap-4 text-sm font-bold uppercase tracking-wider hover:text-primary transition-colors">
                                        {t('about.readHistory', 'Read Full History')} <ArrowRight className={clsx("w-4 h-4 transition-transform", isRTL ? "group-hover:-translate-x-1 rotate-180" : "group-hover:translate-x-1")} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Values Section - Grid with Borders */}
                <div className="bg-white border-t border-slate-100">
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-slate-100 border-b border-slate-100">
                        {values.map((val, idx) => (
                            <div key={idx} className="p-12 group hover:bg-slate-50 transition-colors relative overflow-hidden">
                                <div className="mb-6 text-secondary group-hover:text-primary transition-colors">
                                    {val.icon}
                                </div>
                                <h3 className="text-xl font-bold text-secondary mb-3 font-display">{val.title}</h3>
                                <p className="text-slate-500 text-sm leading-relaxed max-w-[200px]">{val.description}</p>

                                {/* Hover Effect */}
                                <div className={clsx("absolute bottom-0 w-full h-1 bg-primary transform scale-x-0 group-hover:scale-x-100 transition-transform", isRTL ? "right-0 origin-right" : "left-0 origin-left")}></div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};
